import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  loading: boolean;
  contact: FunnelContact | null;
  selectedSex: 'male' | 'female';

  handleSexChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;

  handleUpdateSex(): void;
}

const UpdateSexDialog: React.FC<Props> = ({ open, loading, contact, selectedSex, handleSexChange, handleClose, handleUpdateSex }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const getLinkedInProfileUrl = () => {
    if (!contact) return '';
    if (contact.profileIds?.vanity) {
      return `https://www.linkedin.com/in/${contact.profileIds.vanity}/`;
    }
    return contact.linkedInURL;
  };

  const options = [
    { value: 'female', label: t('femaleGender') },
    { value: 'male', label: t('maleGender') },
  ];

  const contactName = contact?.fullName || `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() || getLinkedInProfileUrl();

  const handleOpenLinkedInProfile = () => {
    window.open(getLinkedInProfileUrl(), '_blank');
  };

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('updateContactSex')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <Stack direction={'row'} gap={1}>
            <Typography className={classes.subTitle}>{t('linkedinProfile')}:</Typography>
            <PaiLink text={contactName} size={'lg'} variant={'primary'} underline={'always'} action={handleOpenLinkedInProfile} />
          </Stack>
          <Divider />
          <PaiSelect label={t('updateSexDescription')} value={selectedSex} variant={'background'} onChange={handleSexChange}>
            {options.map((option) => (
              <PaiMenuItem key={option.value} value={option.value}>
                {option.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('save')} size={'small'} border={'rounded'} loading={loading} disabled={!selectedSex} action={handleUpdateSex} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '24px !important',
    whiteSpace: 'pre-wrap',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default UpdateSexDialog;
